import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

const TOKEN_KEY = 'seller_token';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Axios Instance ───────────────────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    // FIX 13 (Aug 13, 2026, seller-registration audit): declares this
    // client to /auth/login as seller-web, so the backend issues a
    // shorter-lived token (6h) than the mobile app's 7-day default — the
    // token here sits in localStorage (XSS-exposed), so a shorter window
    // reduces stolen-token blast radius. Sent on every request via this
    // shared axios instance, including the login call itself.
    'X-Client-Platform': 'seller-web',
  },
});

// ── Request Interceptor: attach JWT ──────────────────────────────────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response Interceptor: handle 401 ─────────────────────────────────────────
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      // Only redirect if not already on /login to avoid infinite loop
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Token Helpers ────────────────────────────────────────────────────────────
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// ── Auth Endpoints ───────────────────────────────────────────────────────────
export const loginRequest = async (email: string, password: string) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data ?? response;
};

// ── Seller Profile ───────────────────────────────────────────────────────────
export const getSellerProfile = async () => {
  const response = await api.get('/marketplace/sellers/me');
  return response.data ?? response;
};

export const updateSellerProfile = async (data: any) => {
  // FIX 12 (Aug 12, 2026, seller-registration audit): this used to catch a
  // 404 from a PATCH /marketplace/sellers/me that never existed and return
  // a fabricated { message: "Profile updated! (Simulated)" } success — the
  // seller always saw a success toast even though nothing was persisted.
  // The real endpoint now exists in marketplace.py (FIX 12). No more
  // simulated-success fallback: any error (404 or otherwise) must propagate
  // to the caller so Settings.tsx can show the user an honest failure state.
  const response = await api.patch('/marketplace/sellers/me', data);
  return response.data ?? response;
};

// ── Marketplace Listings ──────────────────────────────────────────────────────
export const getMyListings = async () => {
  const response = await api.get('/marketplace/listings/my');
  return response.data ?? response;
};

export const createListing = async (data: any) => {
  const response = await api.post('/marketplace/listings/create', data);
  return response.data ?? response;
};

// ── Full Listing Create (P8-6 Marketplace) ───────────────────────────────────
export interface FullListingPayload {
  title: string;
  author: string;
  category: string;
  condition: string;
  price_lkr: number;
  description: string;
  photos: string[];
  photo_count: number;
  paper_type: string | null;
  discount_pct: number;
  original_price_lkr?: number;
  is_bundle: boolean;
  bundle_book_count?: number;
  is_academic: boolean;
  institution?: string;
  degree_stream?: string;
  academic_year?: string;
  is_weekend_drop: boolean;
  weekend_drop_price?: number;
  flip_test_video_url?: string;
  is_mystery_box: boolean;
  box_weight_kg?: number;
  box_genre_description?: string;
  pin_potha_eligible: boolean;
}

export const createFullListing = async (data: FullListingPayload) => {
  const response = await api.post('/marketplace/listings/create', data);
  return response.data ?? response;
};

export const updateListing = async (id: string, data: any) => {
  const response = await api.patch(`/marketplace/listings/${id}`, data);
  return response.data ?? response;
};

export const deleteListing = async (id: string) => {
  const response = await api.delete(`/marketplace/listings/${id}`);
  return response.data ?? response;
};

export const getMarketplaceListings = async (params?: any) => {
  const response = await api.get('/marketplace/listings', { params });
  return response.data ?? response;
};

// ── Books ────────────────────────────────────────────────────────────────────
export const scanBook = async (isbn: string) => {
  const response = await api.get(`/books/scan/${isbn}`);
  return response.data ?? response;
};

// ── Orders ───────────────────────────────────────────────────────────────────
export const getIncomingOrders = async () => {
  const response = await api.get('/marketplace/orders/incoming');
  return response.data ?? response;
};

export const updateOrderStatus = async (orderId: string, status: string) => {
  const response = await api.patch(`/marketplace/orders/${orderId}/status`, { status });
  return response.data ?? response;
};

export const requestReview = async (orderId: string) => {
  try {
    const response = await api.post(`/marketplace/orders/${orderId}/request-review`);
    return response.data ?? response;
  } catch (error: any) {
    // If endpoint is missing (404), we simulate success as instructed
    if (error.response?.status === 404) {
      return { message: "Review request sent to buyer! (Simulated)" };
    }
    throw error;
  }
};

export const getSellerStats = async () => {
  const response = await api.get('/marketplace/seller/stats');
  return response.data ?? response;
};

export const getSellerRevenue = async (period: 'week' | 'month' | 'year' = 'month') => {
  const response = await api.get('/marketplace/seller/revenue', { params: { period } });
  return response.data ?? response;
};

// ── Seller Ratings & Reviews ───────────────────────────────────────────────────
export const getSellerRatings = async () => {
  try {
    const response = await api.get('/marketplace/seller/ratings');
    return response.data ?? response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // Mock Data Fallback
      return {
        overall: 4.8,
        count: 12,
        breakdown: {
          packaging: 4.9,
          speed: 4.5,
          condition: 4.8,
          communication: 4.7
        },
        distribution: {
          5: 45,
          4: 35,
          3: 15,
          2: 5,
          1: 0
        },
        reviews: [
          {
            id: 'r1',
            buyer_name: 'Kasun Perera',
            rating: 5,
            date: '2026-03-25T10:30:00Z',
            book_title: 'Predator',
            metrics: { packaging: 4.9, speed: 4.8, condition: 5.0, communication: 4.7 },
            content: 'Great seller! Book was in perfect condition. Fast delivery!',
            reply: null
          },
          {
            id: 'r2',
            buyer_name: 'Ashani Silva',
            rating: 4,
            date: '2026-03-22T14:15:00Z',
            book_title: 'Sapiens',
            metrics: { packaging: 4.5, speed: 4.0, condition: 4.5, communication: 4.5 },
            content: 'Good packaging, took a bit long to arrive.',
            reply: 'Thank you for your feedback! We will work on faster delivery.'
          }
        ]
      };
    }
    throw error;
  }
};

export const replyToReview = async (reviewId: string, content: string) => {
  try {
    const response = await api.post(`/marketplace/reviews/${reviewId}/reply`, { content });
    return response.data ?? response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { message: "Reply saved! (Simulated)", reply: content };
    }
    throw error;
  }
};

// ── Messages ────────────────────────────────────────────────────────────────
export const getConversations = async () => {
  try {
    const response = await api.get('/messages/conversations');
    return response.data ?? response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const getConversationMessages = async (id: string) => {
  try {
    const response = await api.get(`/messages/conversations/${id}`);
    return response.data ?? response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const sendConversationMessage = async (id: string, content: string) => {
  const response = await api.post(`/messages/conversations/${id}`, { content });
  return response.data ?? response;
};

export const markConversationRead = async (id: string) => {
  try {
    const response = await api.post(`/messages/conversations/${id}/read`);
    return response.data ?? response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { success: true };
    }
    throw error;
  }
};

// ── Shipment ─────────────────────────────────────────────────────────────────
export const shipOrder = async (orderId: string, trackingNote: string) => {
  const response = await api.post(`/marketplace/orders/${orderId}/confirm-shipped`, {
    tracking_note: trackingNote,
  });
  return response.data ?? response;
};

// FIX 30 (Aug 15, 2026): replaces the old auto-complete-after-7-days behavior
// with a seller-initiated reminder nudge - sends the buyer a notification,
// never changes order status. Backend enforces a 24h cooldown per order
// (shared with the 7-day cron reminder, if Vishwa ever wires one up) so this
// can't be used to spam a buyer.
export const remindBuyer = async (orderId: string) => {
  const response = await api.post(`/marketplace/orders/${orderId}/remind-buyer`);
  return response.data ?? response;
};

// ── Offers ───────────────────────────────────────────────────────────────────
export const getSellerOffers = async () => {
  const response = await api.get('/marketplace/sellers/me/offers');
  return response.data ?? response;
};

export const respondToOffer = async (
  offerId: string,
  responseType: 'accept' | 'reject' | 'counter',
  counterAmount?: number
) => {
  const body: any = { response: responseType };
  if (responseType === 'counter' && counterAmount !== undefined) {
    body.counter_amount = counterAmount;
  }
  const response = await api.post(`/marketplace/offers/${offerId}/respond`, body);
  return response.data ?? response;
};

// ── Boosts ───────────────────────────────────────────────────────────────────
export const initiateBoostListing = async (listingId: string) => {
  const response = await api.post(`/marketplace/listings/${listingId}/boost/initiate`);
  return response.data ?? response;
};

export const getActiveBoosts = async () => {
  const response = await api.get('/marketplace/sellers/me/boosts');
  return response.data ?? response;
};

export default api;
